import { Globe } from "lucide-react";
import { TableCell } from "@/components/ui/table";
import type { SimCard } from "@/lib/sims";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSimNetworkCountry, useSimProfileCountries } from "@/hooks/useSims";

const NO_COUNTRY = "__none__";

/** Picks the country whose networks the SIM lists show. */
export function SimNetworkCountrySelect({
  className = "w-44",
  testId = "sim-network-country",
}: {
  className?: string;
  testId?: string;
}) {
  const [country, setCountry] = useSimNetworkCountry();
  const countries = useSimProfileCountries();
  // A remembered country no profile covers any more is ignored.
  const value =
    country && countries.some((c) => c.code === country) ? country : NO_COUNTRY;
  return (
    <Select
      value={value}
      onValueChange={(v) => setCountry(v === NO_COUNTRY ? null : v)}
    >
      <SelectTrigger className={className} data-testid={testId}>
        <Globe className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_COUNTRY}>Network in…</SelectItem>
        {countries.map((c) => (
          <SelectItem key={c.code} value={c.code}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The selected country, or null (also when no profile covers it). */
export function useSelectedNetworkCountry() {
  const [country] = useSimNetworkCountry();
  const countries = useSimProfileCountries();
  const match = countries.find((c) => c.code === country);
  return match ?? null;
}

/** Table cell with the SIM's profile (hover: where it came from). */
export function SimProfileCell({
  sim,
}: {
  sim: Pick<SimCard, "profileName" | "profileVia" | "ratePlan">;
}) {
  return (
    <TableCell className="whitespace-nowrap" data-testid="sim-profile">
      {sim.profileName ? (
        <span
          title={
            sim.profileVia === "rate_plan"
              ? `From rate plan ${sim.ratePlan}`
              : "Set by hand"
          }
        >
          {sim.profileName}
        </span>
      ) : (
        <span className="text-muted-foreground">–</span>
      )}
    </TableCell>
  );
}

/** Table cell with the network(s) the SIM uses in the selected country. */
export function SimNetworkCell({ network }: { network: string | null }) {
  return (
    <TableCell className="whitespace-nowrap" data-testid="sim-network">
      {network ?? <span className="text-muted-foreground">–</span>}
    </TableCell>
  );
}

/** Column header for the network column: globe + country. */
export function SimNetworkHeaderLabel({ name }: { name: string }) {
  return (
    <span
      className="inline-flex items-center gap-1"
      title={`Network in ${name}`}
    >
      <Globe className="h-3.5 w-3.5" aria-hidden />
      {name}
    </span>
  );
}
